import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Footer from '@/components/shared/Footer';

export default function AboutPage() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-bone" data-testid="about-page">
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <button onClick={() => nav('/')} className="flex items-center gap-2 text-foreground/60 hover:text-foreground font-body text-sm transition-colors" data-testid="about-back-btn">
          <ArrowLeft size={16} /> <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-12 pb-8">
        <p className="font-body text-sm uppercase tracking-[0.2em] text-terracotta font-semibold mb-3">ÜBER MICH</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
          Warum ich Menschen begleite
        </h1>
      </section>

      {/* Geschichte */}
      <section className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <img src="https://images.unsplash.com/photo-1653484596285-7f842613a023?w=800&q=80" alt="Belinda" className="w-full h-[400px] object-cover object-top" />
          </div>
          <div className="space-y-6 font-body text-foreground/60 leading-relaxed">
            <p>
              Ich habe mein Leben lang mit Menschen gearbeitet.<br />
              Nicht mit Zahlen. Nicht mit Konzepten. <strong className="text-foreground/70">Mit Menschen.</strong>
            </p>
            <p>
              Als Führungskraft und Geschäftsführerin durfte ich Mitarbeiter begleiten, entwickeln und fördern.
              In der Erwachsenenbildung habe ich erlebt, wie wertvoll es ist, Menschen nicht einfach Wissen zu vermitteln,
              sondern sie dort abzuholen, wo sie stehen.
            </p>
            <p>
              Durch meine Ausbildung in Mediation habe ich gelernt:
              <em className="block mt-2 text-foreground/50 italic">
                Wirkliche Veränderung entsteht nicht dadurch, dass jemand von aussen sagt, was richtig ist.
                Sie entsteht, wenn ein Mensch seine eigene Erkenntnis findet.
              </em>
            </p>
          </div>
        </div>
      </section>

      {/* Fähigkeit */}
      <section className="bg-warmgray/50">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="max-w-xl space-y-6">
            <h2 className="font-display text-2xl font-medium">Meine besondere Fähigkeit</h2>
            <div className="space-y-3">
              {[
                'Menschen wahrzunehmen.',
                'Zwischen Worten zu hören.',
                'Möglichkeiten zu erkennen.',
                'Stärken sichtbar zu machen.',
              ].map((text, i) => (
                <p key={i} className="font-body text-foreground/60 text-base pl-4 border-l-2 border-terracotta/30">{text}</p>
              ))}
            </div>
            <p className="font-body text-foreground/50 leading-relaxed">
              Und auch schwierige Themen so anzusprechen, dass Entwicklung möglich wird.
            </p>
          </div>
        </div>
      </section>

      {/* Persönlich */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="max-w-xl space-y-6">
          <p className="font-body text-foreground/60 leading-relaxed">
            Ich bin heute eine Frau in der Lebensmitte.<br />
            Mit Erfahrungen. Mit Herausforderungen.<br />
            Mit schönen und schwierigen Lebensphasen.
          </p>
          <div className="bg-gradient-to-r from-terracotta/5 to-amber-50/50 border-l-4 border-terracotta rounded-r-2xl p-6">
            <p className="font-display text-xl font-medium text-foreground/70 italic leading-relaxed">
              Ich weiss: Das Leben wird nicht perfekt.<br />
              Aber es kann bewusst, lebendig und erfüllend werden.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-3xl mx-auto px-6 pb-8">
        <div className="p-4 rounded-2xl bg-warmgray/50 border border-border/30">
          <p className="font-body text-xs text-foreground/40 leading-relaxed">
            Hinweis: Alle Erwähnungen und Empfehlungen basieren auf persönlichen Erfahrungen sowie auf allgemein zugänglichen wissenschaftlichen Daten. Die Inhalte erheben keinen Anspruch auf Vollständigkeit und ersetzen keine professionelle medizinische Beratung.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-12 text-center">
        <Button onClick={() => window.location.href = 'mailto:info@belosophie.com?subject=Erstes Gespräch'} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-10 py-6 text-lg font-medium shadow-lg shadow-terracotta/20 hover:scale-[1.02] transition-all" data-testid="about-cta-btn">
          Lernen wir uns kennen <ArrowRight size={20} className="ml-2" />
        </Button>
        <p className="font-body text-xs text-foreground/30 mt-4">info@belosophie.com</p>
      </section>

      <Footer />
    </div>
  );
}
